import { Pagination, ThemeProvider } from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import CompanyApi from "../../../../api/companyApi";
import UsersApi from "../../../../api/usersApi";
import { ReactComponent as MagnifierIcon } from "../../../../assets/Specialists/magnifier.svg";
import Input from "../../../../components/Input/Input";
import ModalWindow from "../../../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../../components/StandartButton/StandartButton";
import {
  Company,
  Project,
  TypeCompanyEmployee,
} from "../../../../types/Company";
import { getTranslation } from "../../../../utils/getTranslation";
import { theme } from "../../../../utils/theme";
import AddSpecialistsToCompany from "../AddSpecialistsToCompany/AddSpecialistsToCompany";
import UpdateCompany from "../UpdateCompany/UpdateCompany";
import EmployerCard from "./EmployerCard/EmployerCard";
import s from "./Participants.module.css";

type ParticipantsProps = {
  selectedCompany: Company;
  setSelectedCompany: Dispatch<SetStateAction<Company | null>>;
  setCompaniesData: Dispatch<SetStateAction<Company[] | null>>;
  companyProjects: Project[] | null;
};

const Participants = ({
  selectedCompany,
  setSelectedCompany,
  setCompaniesData,
  companyProjects,
}: ParticipantsProps) => {
  const token = localStorage.getItem("token");
  const participantsLimit = 10;
  const [companyEmployers, setCompanyEmployers] = useState<
    TypeCompanyEmployee[] | null
  >([]);
  const [search, setSearch] = useState("");

  const [filteredEmployers, setFilteredEmployers] = useState<
    TypeCompanyEmployee[] | null
  >([]);
  const [projectFilterValue, setProjectFilterValue] = useState<
    string | string[]
  >([]);
  const [roleFilterValue, setRoleFilterValue] = useState<string | string[]>([]);
  const [avatars, setAvatars] = useState<{ [userId: string]: string }>({});
  const [changeOwnerModal, setChangeOwnerModal] = useState(false);
  const [addSpecialistModal, setAddSpecialistModal] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0 });
  const selectedCompanyIdRef = useRef(selectedCompany._id);

  const roleData = [
    { lable: "employee", value: "employee" },
    { lable: "manager", value: "manager" },
    { lable: "owner", value: "owner" },
    { lable: "specialist", value: "specialist" },
  ];

  const applyFilters = useCallback(
    (participants: TypeCompanyEmployee[] | null) => {
      if (!participants) {
        setFilteredEmployers(participants);
        return;
      }

      let filteredEmployees = participants;

      if (search) {
        const searchTerm = search.toLowerCase();
        filteredEmployees = filteredEmployees.filter((employer) => {
          const fullname = `${getTranslation(
            employer.user.name,
          )} ${getTranslation(employer.user.surname)}`.toLowerCase();
          return fullname.includes(searchTerm);
        });
      }

      if (roleFilterValue.length) {
        filteredEmployees = filteredEmployees.filter((employee) =>
          roleFilterValue.includes(employee.role.value),
        );
      }

      if (projectFilterValue.length && companyProjects) {
        filteredEmployees = filteredEmployees.filter((employee) => {
          return employee.user.projects
            .map((item) => item._id)
            .some((project) => projectFilterValue.includes(project));
        });
      }

      setFilteredEmployers(filteredEmployees);
    },
    [companyProjects, projectFilterValue, roleFilterValue, search],
  );

  const getParticipantsAvatars = async (
    participants: TypeCompanyEmployee[] | null,
  ) => {
    if (!participants || participants.length === 0) {
      setAvatars({});
      return;
    }

    const promises = participants.map((employer) =>
      UsersApi.getUserPublicAvatarAndName(employer.user._id),
    );
    const responses = await Promise.all(promises);
    const avatarsData = responses.reduce(
      (acc, response) => {
        const avatar = response.avatarS3 || response.avatar;
        if (avatar) {
          acc[response._id] = avatar;
        }
        return acc;
      },
      {} as { [userId: string]: string },
    );

    setAvatars(avatarsData);
  };

  useEffect(() => {
    if (token) {
      if (selectedCompanyIdRef.current !== selectedCompany._id) {
        selectedCompanyIdRef.current = selectedCompany._id;

        if (pagination.page !== 1) {
          setPagination((prev) => ({ ...prev, page: 1 }));
          return;
        }
      }

      const makeAsync = async () => {
        const companyEmployersResponse =
          await CompanyApi.getCompanyParticipants(
            token,
            selectedCompany._id,
            pagination.page,
            participantsLimit,
          );

        if (companyEmployersResponse.status) {
          const participants = companyEmployersResponse.participants;

          setCompanyEmployers(participants);
          setPagination((prev) => ({
            ...prev,
            totalPages: Math.ceil(
              (companyEmployersResponse.totalAmount ?? 0) / participantsLimit,
            ),
          }));
        }
      };

      makeAsync();
    }
  }, [pagination.page, selectedCompany._id, token]);

  useEffect(() => {
    applyFilters(companyEmployers);
  }, [applyFilters, companyEmployers]);

  useEffect(() => {
    getParticipantsAvatars(companyEmployers);
  }, [companyEmployers]);

  const filterEmployers = (value: string) => {
    setSearch(value);
  };

  return (
    <div className={s.container}>
      <div className={s.mainContentContainer}>
        <div className={s.employersTableContainer}>
          <div className={s.headBlock}>
            <div className={s.filterBlock}>
              <div className={s.searchInputBlock}>
                <Input
                  inputValue={search}
                  onChangeInput={(value) => filterEmployers(value)}
                  isVisible
                  required
                  placeholder={"Search..."}
                />
                <MagnifierIcon className={s.magnifierIcon} />
              </div>
              <div className={s.selectFilterBlock}>
                <span className={s.filterTitle}>Filter by</span>
                <div className={s.selectInputBlock}>
                  <div className={s.multiSelectBlock}>
                    <MultipleSelect
                      data={
                        companyProjects
                          ? companyProjects.map((item) => ({
                              value: item._id,
                              lable: getTranslation(item.title, "en"),
                            }))
                          : []
                      }
                      multiplie={true}
                      value={projectFilterValue}
                      setValue={(value) => setProjectFilterValue(value ?? [])}
                      lable={"Program"}
                      checkBox={true}
                    />
                  </div>
                  <div className={s.multiSelectBlock}>
                    <MultipleSelect
                      data={roleData}
                      multiplie={true}
                      value={roleFilterValue}
                      setValue={(value) => setRoleFilterValue(value ?? [])}
                      lable={"Role"}
                      checkBox={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={s.actionBlock}>
              <StandartButton
                action={() => setChangeOwnerModal(true)}
                buttonTitle="Update company"
                height="40px"
              />
              <StandartButton
                action={() => setAddSpecialistModal(true)}
                buttonTitle="Invite specialists"
                height="40px"
              />
            </div>
          </div>
          <div className={s.lableTableBlock}>
            <span className={s.nameTableLable}>Name</span>
            <span className={s.roleTableLable}>Role</span>
            <span className={s.programTableLable}>Programs</span>
            <span className={s.emailTableLable}>Email</span>
          </div>
          <div className={s.tableBlock}>
            {filteredEmployers &&
              filteredEmployers.map((item, index) => (
                <div
                  key={item.user._id + index}
                  className={s.employeeCardContainer}
                  style={{
                    borderRadius:
                      index === filteredEmployers.length - 1
                        ? "0 0 20px 20px"
                        : "0",
                    backgroundColor:
                      (index + 1) % 2 === 0
                        ? "rgba(63, 176, 130, 0.1)"
                        : "rgb(46, 46, 46)",
                  }}
                >
                  <EmployerCard
                    companyId={selectedCompany._id}
                    userId={item.user._id}
                    email={item.user.email}
                    fullName={
                      getTranslation(item.user.name) +
                      " " +
                      getTranslation(item.user.surname)
                    }
                    program={item.user.projects}
                    role={item.role.value}
                    avatar={avatars[item.user._id]}
                    setFilteredEmployers={setFilteredEmployers}
                    companyProjects={companyProjects}
                  />
                </div>
              ))}
          </div>
          {pagination.totalPages > 1 && (
            <div className={s.paginationBlock}>
              <ThemeProvider theme={theme}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "white",
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      color: "white",
                      backgroundColor: "primary.main",
                    },
                  }}
                  onChange={(_, value) =>
                    setPagination((prev) => ({ ...prev, page: value }))
                  }
                />
              </ThemeProvider>
            </div>
          )}
        </div>
      </div>
      <ModalWindow
        isOpen={changeOwnerModal}
        setIsOpen={setChangeOwnerModal}
        maxWidth="fit-content"
      >
        <UpdateCompany
          selectedCompany={selectedCompany}
          setUpdateModalVisible={setChangeOwnerModal}
          setSelectedCompany={setSelectedCompany}
          setCompaniesData={setCompaniesData}
        />
      </ModalWindow>
      <ModalWindow
        isOpen={addSpecialistModal}
        setIsOpen={setAddSpecialistModal}
        maxWidth="fit-content"
      >
        <AddSpecialistsToCompany
          selectedCompany={selectedCompany}
          setModalVisible={setAddSpecialistModal}
        />
      </ModalWindow>
    </div>
  );
};

export default Participants;
